//////////////////////////////////////////////////////////////////////
//
// These types must be kept in sync with the backend API types
// (rust/server/src/api/structs.rs).
//
import { useState, useEffect } from 'react';

import Link from 'next/link';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import useSWR from 'swr';

import { API_URL } from './consts';
import { buildDocTree } from './docThreadView';
import type { OAuthIdentityInfo, OAuthProvider } from './oauth';

export { buildDocThreadView } from './docThreadView';
export type { DocThreadView } from './docThreadView';

export type PublicDocId = string & { readonly __brand: 'PublicDocId' };

export function asPublicDocId(value: string): PublicDocId {
  return value as PublicDocId;
}

/**
 * Runtime document visibility identifiers shared with the backend contract.
 * Must be kept in sync with rust/server/src/api/structs.rs.
 */
export const DOC_VISIBILITY = {
  Public: 'public',
  Friends: 'friends',
  Private: 'private',
} as const;
export type DocVisibility = (typeof DOC_VISIBILITY)[keyof typeof DOC_VISIBILITY];

/** Enumerates document visibility values in menu and validation order. */
export const DOC_VISIBILITY_VALUES = [
  DOC_VISIBILITY.Public,
  DOC_VISIBILITY.Friends,
  DOC_VISIBILITY.Private,
] as const satisfies readonly DocVisibility[];

/** Assigns a numeric restrictiveness rank to each document visibility. */
export const DOC_VISIBILITY_ORDER: Record<DocVisibility, number> = {
  [DOC_VISIBILITY.Public]: 0,
  [DOC_VISIBILITY.Friends]: 1,
  [DOC_VISIBILITY.Private]: 2,
};

/**
 * Runtime follow action identifiers shared with the backend contract.
 * Must be kept in sync with rust/server/src/api/structs.rs.
 */
export const UPDATE_FOLLOW_ACTION = {
  Follow: 'follow',
  Unfollow: 'unfollow',
  Update: 'update',
} as const;
export type UpdateFollowAction = (typeof UPDATE_FOLLOW_ACTION)[keyof typeof UPDATE_FOLLOW_ACTION];

/**
 * Runtime collection action identifiers shared with the backend contract.
 * Must be kept in sync with rust/server/src/api/structs.rs.
 */
export const UPDATE_COLLECTIONS_ACTION = {
  Add: 'add',
  Remove: 'remove',
} as const;
export type UpdateCollectionsAction =
  (typeof UPDATE_COLLECTIONS_ACTION)[keyof typeof UPDATE_COLLECTIONS_ACTION];

// Vote action: what the user explicitly did
// IMPORTANT: Must be kept in sync with backend VoteAction enum
// in rust/base/src/kb/vote/mod.rs
export type VoteAction = 'upvote' | 'downvote' | 'remove';

// Vote state: the current state of a user's vote
// IMPORTANT: Must be kept in sync with backend VoteState enum
// in rust/base/src/kb/vote/mod.rs
export type VoteState = 'up' | 'down';

// Vote info safe for any authenticated user's API response
// IMPORTANT: Must be kept in sync with backend DisclosedVoteInfo struct
// in rust/base/src/kb/vote/mod.rs
export interface DisclosedVoteInfo {
  score: number;
  user_vote: VoteState | null;
}

// Background task statuses used by the doc live-update websocket.
// IMPORTANT: Must be kept in sync with rust/work/src/bg/mod.rs BgTaskUpdate.
export enum BgTaskStatus {
  Reading = 'Reading',
  ReadingThread = 'ReadingThread',
  Abstain = 'Abstain',
  Researching = 'Researching',
  SearchingProducts = 'SearchingProducts',
  AnalyzingSearchResults = 'AnalyzingSearchResults',
  FetchingProductDetails = 'FetchingProductDetails',
  FetchingReviews = 'FetchingReviews',
  LoadingSiteInfo = 'LoadingSiteInfo',
  ReformulatingQuery = 'ReformulatingQuery',
  ReformulatingProductTitle = 'ReformulatingProductTitle',
  Recommending = 'Recommending',
  Replying = 'Replying',
  Answered = 'Answered',
  Replied = 'Replied',
  Completed = 'Completed',
  Failed = 'Failed',
  WritingGuides = 'WritingGuides',
  AnalyzingFeedback = 'AnalyzingFeedback',
}

// A concept reference, which is a unique identifier for a concept.  Used
// for both general concepts (topics) and specific products.
export const DISPLAY_IMAGE_CDN = {
  Amazon: 'amazon',
  Walmart: 'walmart',
  Shopify: 'shopify',
  Static: 'static',
} as const;
export type DisplayImageCdn = (typeof DISPLAY_IMAGE_CDN)[keyof typeof DISPLAY_IMAGE_CDN];

export interface DisplayImage {
  url: string;
  cdn: DisplayImageCdn;
}

export interface DisplayConceptRef {
  cref: string;
  display: string;
  description: string; // Contains descriptive text (formerly overview)
  mention?: string; // For when this concept is mentioned in text
  preview_image?: DisplayImage; // Preview image for this concept if available
  is_product: boolean; // Indicates if this concept is a product (SKU)
}

// TODO(1): use consistently; there are places in the code base where ad-hoc
// structures are used instead of this.
export interface DisplayConceptDef {
  key: string;
  display_name: string;
  refd?: {
    gloss?: string;
    genus?: string;
    constraints?: Record<string, string>;
  };
  sup?: string[];
  sub?: string[];
}

// Utility function to convert DisplayConceptDef to DisplayConceptRef
export function convertConceptDefToRef(cdef: DisplayConceptDef): DisplayConceptRef {
  return {
    cref: cdef.key,
    display: cdef.display_name,
    description: cdef.refd?.gloss || '',
    is_product: cdef.key.startsWith('/sku/'), // TODO(1): This should come from backend once is_product field is available
  };
}

// Provenance information for documents and feed items
interface Provenance {
  createdon: string;
  creator: string;
}

// Structured document types matching the Rust StructuredDocContent system
// IMPORTANT: Must be kept in sync with rust/base/src/kb/docs/structured_content.rs
export interface StructuredDoc {
  blocks: ContentBlock[];
}

// Block-level elements in the document
export type ContentBlock =
  | { type: 'paragraph'; inline_elements: InlineElement[] }
  | { type: 'heading'; level: number; inline_elements: InlineElement[] }
  | { type: 'list'; ordered: boolean; items: StructuredListItem[] }
  | { type: 'table'; headers: TableRow; rows: TableRow[] }
  | { type: 'product_comparison_table'; rows: ProductComparisonRow[] }
  | { type: 'block_quote'; blocks: ContentBlock[] }
  | { type: 'code_block'; language?: string; code: string }
  | { type: 'thematic_break' };

// List item (can contain nested blocks)
export interface StructuredListItem {
  blocks: ContentBlock[];
}

// Table types
export interface TableRow {
  cells: TableCell[];
}

export interface TableCell {
  inline_elements: InlineElement[];
}

// Inline elements within a block
export type InlineElement =
  | { type: 'text'; content: string }
  | { type: 'concept_link'; text: InlineElement[]; cref: string }
  | { type: 'link'; text: InlineElement[]; url: string; title?: string }
  | { type: 'image'; alt: string; url: string; title?: string }
  | { type: 'emphasis'; content: InlineElement[] }
  | { type: 'strong'; content: InlineElement[] }
  | { type: 'code'; content: string }
  | { type: 'line_break' }
  | { type: 'soft_break' };

// Product comparison row
export interface ProductComparisonRow {
  product_cref: string;
  product_name: string;
  comments: string;
}

// A document or post, as displayed to the user.
// All fields are actively used in the frontend based on audit performed
export interface DisplayDoc {
  id: PublicDocId;
  text: string;
  topic: DisplayConceptRef | null; // Always present from backend, but can be null
  aspect: DisplayConceptRef | null; // Always present from backend, but can be null
  citations: DisplayCitation[];
  provenance: Provenance;
  visibility: DocVisibility;
  mentioned_concepts: DisplayConceptRef[];
  // TODO(1): reconsider if this is the best way to attach to a document
  suggested_questions: string[];
  // Indicates if this is a feedback post (contains @feedback tag)
  is_feedback_post: boolean;
  // Summary text for guide documents (optional)
  summary?: string;
  // Structured document content (new system)
  structured_content?: StructuredDoc;
  // Vote info for authenticated users (null/absent for unauthenticated)
  // IMPORTANT: Must be kept in sync with backend DisplayDoc.vote_info
  // in rust/server/src/api/structs.rs
  vote_info?: DisclosedVoteInfo | null;
}

export interface DisplayCitation {
  doc: PublicDocId;
  rel: string;
}

// For attaching children to a document node.
export interface DocNode extends DisplayDoc {
  children: DocNode[];
}

// A single topic entry in a thread's table of contents
export interface ThreadTopicEntry {
  doc_id: PublicDocId;
  summary: string;
}

// Table of contents for a thread
export interface ThreadTableOfContents {
  topics: ThreadTopicEntry[];
}

// A thread of documents with a table of contents
interface DisplayThreadWithToC {
  docs: DisplayDoc[];
  table_of_contents?: ThreadTableOfContents;
}

// Define type of the json structure we get back.
// IMPORTANT: Keep in sync with backend DisplayProduct in rust/server/src/api/structs.rs.
export interface DisplayProduct {
  id: string;
  marketplace: string;
  title: string;
  orig_title?: string;
  description: string;
  vendor: string;
  images: DisplayImage[];
  price?: number;
  currency?: string;
  category_path?: string;
  product_url?: string;
  customer_rating?: number;
  num_reviews?: number;
  stock_status?: string;
  tags: string[];
  buy_at_label: string;
}

// A thread of posts
export interface DisplayThread {
  docs: DisplayDoc[];
}

// A feed item
export interface FeedItem {
  datetime: string; // timestamp
  thread: DisplayThread; // In chrono order
  creators: string[];
  topics: DisplayConceptRef[];
  products: DisplayProduct[];
  suggested_questions: string[];
  justification: FeedItemJustification;
  summary?: DisplayDoc; // Thread summary for card display
}

// Justification for a feed item
interface FeedItemJustification {
  // In case of the user being mentioned
  mentioner?: string;

  // In case of this being content the user follows
  friend?: string; // Follower or followee
  topic?: DisplayConceptRef;
  aspect?: DisplayConceptRef;

  // In case of this being a thread the user started or
  // added questions to
  is_owned: boolean;
}

// Info about a follow for a user (typically the current user).  Contains
// the username of the followed user, and the topics for the follow.
export interface DisplayUserFollow {
  username: string;
  topics: DisplayConceptRef[];
}

// Some additional info for the user that's generally useful in the UI.
interface DisplayCollection {
  name: string;
  docs: PublicDocId[];
  products: DisplayConceptRef[];
}

// A consolidated grouping of the thick data for all the items in the
// user's collections.
export interface CollectionsPayload {
  docs: DisplayDoc[];
  products: DisplayProduct[];
}

interface OAuthIdentity {
  provider: OAuthProvider;
  provider_user_id: string;
  email?: string;
  name?: string;
  picture?: string;
}

export interface DisplayAvatar {
  url: string;
}

/**
 * Runtime avatar source identifiers shared with the backend contract.
 * Must be kept in sync with rust/base/src/kb/user/mod.rs.
 */
export const AVATAR_SOURCE = {
  DefaultPlaceholder: 'default_placeholder',
  GeneratedInitials: 'generated_initials',
} as const;
export type AvatarSource = (typeof AVATAR_SOURCE)[keyof typeof AVATAR_SOURCE];

export const AVATAR_SOURCE_DISPLAY_ORDER: readonly AvatarSource[] = [
  AVATAR_SOURCE.DefaultPlaceholder,
  AVATAR_SOURCE.GeneratedInitials,
] as const;

export function assertAvatarSourceDisplayOrderIsExhaustive(): void {
  const all = Object.values(AVATAR_SOURCE);
  const order = [...AVATAR_SOURCE_DISPLAY_ORDER];

  const unique = new Set(order);
  if (unique.size !== order.length) {
    throw new Error('Duplicate avatar source in display order');
  }

  const missing = all.filter((source) => !unique.has(source));
  const extra = order.filter((source) => !all.includes(source));

  if (missing.length > 0 || extra.length > 0) {
    throw new Error(
      `Avatar source display order mismatch: missing=${missing.join(',')} extra=${extra.join(',')}`
    );
  }
}

assertAvatarSourceDisplayOrderIsExhaustive();

export interface DefaultPlaceholderAvatarConfig {}

export interface GeneratedInitialsAvatarConfig {
  initials: string;
  background_color: string;
}

export interface AvatarSettingsSource<TConfig> {
  available: boolean;
  config: TConfig;
}

export interface AvatarSettingsOptions {
  default_placeholder: AvatarSettingsSource<DefaultPlaceholderAvatarConfig>;
  generated_initials: AvatarSettingsSource<GeneratedInitialsAvatarConfig>;
}

export interface AvatarSettingsSuggestions {
  generated_initials?: string;
}

export interface AvatarSettingsResponse {
  selected: AvatarSource;
  options: AvatarSettingsOptions;
  generated_initials_preset_colors: string[];
  suggestions: AvatarSettingsSuggestions;
}

export interface AvatarSettingsPatchConfigs {
  default_placeholder?: DefaultPlaceholderAvatarConfig;
  generated_initials?: GeneratedInitialsAvatarConfig;
}

export interface PatchAvatarSettingsRequest {
  selected?: AvatarSource;
  configs?: AvatarSettingsPatchConfigs;
}

export const AVATAR_VALIDATION_ERROR_CODE = {
  Required: 'required',
  TooLong: 'too_long',
  InvalidCharacters: 'invalid_characters',
  InvalidHexColor: 'invalid_hex_color',
} as const;
export type AvatarValidationErrorCode =
  (typeof AVATAR_VALIDATION_ERROR_CODE)[keyof typeof AVATAR_VALIDATION_ERROR_CODE];

export interface AvatarValidationErrors {
  initials?: AvatarValidationErrorCode;
  background_color?: AvatarValidationErrorCode;
}

// Avatar validation error payload returned by avatar settings and preview endpoints.
// IMPORTANT: Must be kept in sync with backend AvatarValidationErrorResponse struct
// in rust/server/src/api/structs.rs
export interface AvatarValidationErrorResponse {
  success: false;
  status: 'invalid_config';
  errors: AvatarValidationErrors;
}

// Avatar preview request payload shared with the backend avatar preview API.
// IMPORTANT: Must be kept in sync with backend AvatarPreviewRequest enum
// in rust/server/src/api/structs.rs
export type AvatarPreviewRequest =
  | {
      source: typeof AVATAR_SOURCE.DefaultPlaceholder;
      config: DefaultPlaceholderAvatarConfig;
    }
  | {
      source: typeof AVATAR_SOURCE.GeneratedInitials;
      config: GeneratedInitialsAvatarConfig;
    };

type AvatarPreviewResult =
  | {
      success: true;
      svg: Blob;
    }
  | AvatarValidationErrorResponse;

export function resolveEffectiveAvatarUrl(url: string): string {
  if (url.startsWith('/user/avatar/')) {
    return `${API_URL}${url}`;
  }

  return url;
}

export interface DisplayUser {
  username: string;
  fullname: string;
  email: string;
  pending_email?: string;
  link: string;
  avatar: DisplayAvatar;
  has_been_welcomed: boolean;
  may_invite: boolean;
  followed_topics: DisplayConceptRef[];
  followed_users: DisplayUserFollow[];
  collections: DisplayCollection[];
  oauth_identities?: Record<string, OAuthIdentity>;
  has_password_auth: boolean;
  password_state: string;
  has_set_password: boolean;
  profile_discoverability: ProfileDiscoverability;
  marketplace_preference: MarketplacePreference;
  // Admin flag computed by backend (backend is security boundary)
  // IMPORTANT: Keep in sync with backend DisplayUser struct in rust/server/src/api/structs.rs
  is_admin: boolean;
}

/**
 * Runtime profile discoverability identifiers shared with the backend contract.
 * Must be kept in sync with rust/server/src/api/structs.rs.
 */
export const PROFILE_DISCOVERABILITY = {
  Public: 'public',
  Connections: 'connections',
  Private: 'private',
} as const;
export type ProfileDiscoverability =
  (typeof PROFILE_DISCOVERABILITY)[keyof typeof PROFILE_DISCOVERABILITY];

/** Enumerates profile discoverability values in UI display order. */
export const PROFILE_DISCOVERABILITY_VALUES = [
  PROFILE_DISCOVERABILITY.Public,
  PROFILE_DISCOVERABILITY.Connections,
  PROFILE_DISCOVERABILITY.Private,
] as const satisfies readonly ProfileDiscoverability[];

/**
 * Runtime marketplace identifiers shared with the backend contract.
 * Must be kept in sync with rust/base/src/kb/sku/marketplace.rs.
 */
export const MARKETPLACE = {
  Amazon: 'amazon',
  Walmart: 'walmart',
  Shopify: 'shopify',
} as const;
export type Marketplace = (typeof MARKETPLACE)[keyof typeof MARKETPLACE];

/** Maps marketplace identifiers to the labels shown in the frontend. */
export const MARKETPLACE_DISPLAY_NAMES: Record<Marketplace, string> = {
  [MARKETPLACE.Amazon]: 'Amazon',
  [MARKETPLACE.Walmart]: 'Walmart',
  [MARKETPLACE.Shopify]: 'Shopify',
};

/** Lists all supported marketplaces in the order expected by marketplace controls. */
export const ALL_MARKETPLACES = [
  MARKETPLACE.Amazon,
  MARKETPLACE.Walmart,
  MARKETPLACE.Shopify,
] as const satisfies readonly Marketplace[];

// Shopping marketplace preference
// IMPORTANT: Must be kept in sync with backend MarketplaceAffinity struct
// in rust/base/src/integrations/orchestrator/types.rs
export interface MarketplacePreference {
  preferred: Marketplace[];
  avoided: Marketplace[];
}

// Minimal user search result
// IMPORTANT: Must be kept in sync with backend UserSearchResult struct
// in rust/server/src/api/structs.rs
export interface UserSearchResult {
  username: string;
  fullname: string;
  i_follow: boolean;
  follows_me: boolean;
  mutual: boolean;
  shared_topics: DisplayConceptRef[];
  shared_topics_count: number;
}

// Public profile view
// IMPORTANT: Must be kept in sync with backend PublicUserProfile struct
// in rust/server/src/api/structs.rs
export interface PublicUserProfile {
  username: string;
  fullname: string;
  followed_topics: DisplayConceptRef[];
  followed_users: DisplayUserFollow[];
  i_follow: boolean;
  follows_me: boolean;
  mutual: boolean;
}

// A request to follow or unfollow topics
interface UpdateFollowTopicsRequest {
  username: string;
  add_topics?: string[];
  rem_topics?: string[];
}

// A request to follow or unfollow a user.
interface UpdateUserFollowsRequest {
  username: string;
  follow: string;
  action: UpdateFollowAction;
  topics?: string[];
}

// A unified request to update follows (both topics and users)
interface UpdateFollowsRequest {
  action: UpdateFollowAction;
  username_to_follow?: string;
  topics?: string[];
}

//////////////////////////////////////////////////////////////////////
//
// Shared components that call the backend
//

// Shared fetcher with error handling
export const urlFetcher = (url: string) =>
  fetch(url, {
    credentials: 'include', // Add this line
  }).then((res) => {
    if (!res.ok) throw Error(res.statusText);
    return res.json();
  });

// Not sure how widely these are used.  Also not sure if they should be
// used vs. the useSWR hook.
export enum LoadStatus {
  NoArg,
  Error,
  IsLoading,
  EmptyResponse,
  Success,
}

/**
 * Generic hook for fetching data with LoadStatus handling.
 *
 * @param url - The URL to fetch data from
 * @param argCheck - Whether the arguments are valid for fetching
 * @param dataTransform - Optional transform function applied to fetched data.
 *   IMPORTANT: This function should be pure and not capture changing values.
 *   If your transform depends on reactive state, wrap it with useCallback:
 *   ```
 *   const transform = useCallback((data) =>
 *     data.filter(x => x.id === selectedId), [selectedId]
 *   );
 *   ```
 * @returns A tuple of [LoadStatus, transformed data or null]
 */
export function useGetData<T>(
  url: string,
  argCheck: boolean,
  dataTransform?: (data: any) => T
): [LoadStatus, T | null] {
  const { data, error, isLoading } = useSWR(url, urlFetcher);

  const [status, setStatus] = useState<LoadStatus>(LoadStatus.IsLoading);
  const [result, setResult] = useState<T | null>(null);

  useEffect(() => {
    if (!argCheck) {
      setStatus(LoadStatus.NoArg);
      setResult(null);
    } else if (error) {
      setStatus(LoadStatus.Error);
      setResult(null);
    } else if (isLoading) {
      setStatus(LoadStatus.IsLoading);
      setResult(null);
    } else if (!data || (typeof data === 'string' && data.length === 0)) {
      setStatus(LoadStatus.EmptyResponse);
      setResult(null);
    } else {
      setStatus(LoadStatus.Success);
      // Apply transformation if provided, otherwise use data as-is
      const transformedData = dataTransform ? dataTransform(data) : (data as T);
      setResult(transformedData);
    }
    // dataTransform intentionally excluded from deps - it's logically stable and callers
    // pass simple transform functions that don't capture changing values. Including it
    // causes infinite re-renders when callers use inline arrow functions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argCheck, data, error, isLoading]);

  return [status, result];
}

// Load a concept definition from the backend.
// Safe to use inline arrow function here since the transform doesn't capture changing values.
// useGetData ignores dataTransform changes to prevent re-render loops.
export function useGetCDef(topic: string): [LoadStatus, DisplayConceptDef | null] {
  const url = topic ? `${API_URL}/schema/${topic}` : '';
  return useGetData<DisplayConceptDef>(url, topic !== '', (data) => JSON.parse(data));
}

interface FollowedUsersListProps {
  following: DisplayUserFollow[];
}

export const FollowedUsersList: React.FC<FollowedUsersListProps> = ({ following }) => {
  return (
    <nav aria-label="Following list">
      <ul>
        {following.map((follow, index) => (
          <li key={index}>
            <Link href={`/citizen/${encodeURIComponent(follow.username)}`}>{follow.username}</Link>
            {follow.topics.length > 0 && (
              <p>
                <span id={`topics-label-${index}`}>Topics:</span>
                <span aria-labelledby={`topics-label-${index}`}>
                  {follow.topics.map((c) => c.display).join(', ')}
                </span>
              </p>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

//////////////////////////////////////////////////////////////////////
//
// Shared utility functions for working with documents
//

// Recursively collect all nodes in a subtree
const collectSubtreeNodes = (node: DocNode): DocNode[] => {
  const nodes = [node];
  node.children.forEach((child) => {
    nodes.push(...collectSubtreeNodes(child));
  });
  return nodes;
};

// Filter out feedback posts and their entire subtrees, return flat array
export const filterFeedbackSubtrees = (docs: DisplayDoc[]): DisplayDoc[] => {
  const docTree = buildDocTree(docs);
  const filteredNodes: DocNode[] = [];

  // For each root node, check if it's a feedback post
  docTree.forEach((rootNode) => {
    if (rootNode.is_feedback_post) {
      // Skip this entire subtree
      return;
    }

    // For non-feedback root nodes, recursively filter their children
    const filterChildren = (node: DocNode): DocNode => {
      const filteredChildren = node.children
        .filter((child) => !child.is_feedback_post)
        .map((child) => filterChildren(child));

      return { ...node, children: filteredChildren };
    };

    filteredNodes.push(filterChildren(rootNode));
  });

  // Flatten back to DisplayDoc array
  const result: DisplayDoc[] = [];
  filteredNodes.forEach((node) => {
    result.push(...collectSubtreeNodes(node));
  });

  return result;
};

//////////////////////////////////////////////////////////////////////
//
// Shared functions for calling the backend.
//

export async function updateFollows(
  req_fields: Partial<UpdateFollowsRequest>
): Promise<DisplayUser> {
  const request: UpdateFollowsRequest = {
    action: UPDATE_FOLLOW_ACTION.Follow,
    ...req_fields,
  };

  const resp = await fetch(`${API_URL}/user/update_follows`, {
    method: 'POST',
    credentials: 'include', // Add this
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!resp.ok) {
    throw new Error(`Error updating follows: ${resp.statusText}`);
  }

  return resp.json();
}

// Cast or remove a vote on a document
// IMPORTANT: Must be kept in sync with backend CastVoteRequest/CastVoteResponse/CastVoteStatus
// in rust/server/src/api/vote.rs

// Status codes for cast vote operations
// IMPORTANT: Must be kept in sync with backend CastVoteStatus enum
// in rust/server/src/api/vote.rs
export type CastVoteStatus = 'success' | 'rate_limited' | 'self_vote_not_allowed' | 'doc_not_found';

// Response for cast vote operation
// IMPORTANT: Must be kept in sync with backend CastVoteResponse struct
// in rust/server/src/api/vote.rs
export interface CastVoteResponse {
  status: CastVoteStatus;
  vote_info?: DisclosedVoteInfo | null;
}

export async function castVote(
  docId: PublicDocId,
  voteAction: VoteAction
): Promise<CastVoteResponse> {
  const resp = await fetch(`${API_URL}/doc/vote`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ doc_id: docId, vote_action: voteAction }),
  });

  if (!resp.ok) {
    throw new Error(`Error casting vote: ${resp.statusText}`);
  }

  return resp.json();
}

// Update the user's profile discoverability setting
export async function setProfileDiscoverability(
  discoverability: ProfileDiscoverability
): Promise<DisplayUser> {
  const resp = await fetch(`${API_URL}/user/set_discoverability`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ discoverability }),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  return resp.json();
}

// Update the user's marketplace preference setting
export async function setMarketplacePreference(
  marketplace_preference: MarketplacePreference
): Promise<DisplayUser> {
  const resp = await fetch(`${API_URL}/user/set_marketplace_preference`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ marketplace_preference }),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  return resp.json();
}

// Load the current avatar settings and available avatar source options.
export async function getAvatarSettings(): Promise<AvatarSettingsResponse> {
  const resp = await fetch(`${API_URL}/user/avatar-settings`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  return resp.json();
}

// Update the selected avatar source and its editable configuration.
export async function patchAvatarSettings(
  request: PatchAvatarSettingsRequest
): Promise<AvatarSettingsResponse | AvatarValidationErrorResponse> {
  const resp = await fetch(`${API_URL}/user/avatar-settings`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  return resp.json();
}

// Request an avatar preview, returning either rendered SVG or validation errors.
export async function postAvatarPreview(
  request: AvatarPreviewRequest
): Promise<AvatarPreviewResult> {
  const resp = await fetch(`${API_URL}/user/avatar-preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  const contentType = resp.headers.get('content-type') ?? '';
  if (contentType.startsWith('image/svg+xml')) {
    return {
      success: true,
      svg: await resp.blob(),
    };
  }

  return resp.json();
}

// Response from password strength check
interface PasswordStrengthResponse {
  strength: number;
  is_acceptable: boolean;
}

// Registration status codes matching RegistrationStatus enum on the backend
// IMPORTANT: Keep in sync with rust/server/src/auth.rs RegistrationStatus
export const RegistrationStatus = {
  Success: 'success',
  VerificationEmailSendFailed: 'verification_email_send_failed',
  UnsupportedFormat: 'unsupported_format',
  UsernameInvalid: 'username_invalid',
  UsernameTaken: 'username_taken',
  InvalidInviteCode: 'invalid_invite_code',
  PasswordInsecure: 'password_insecure',
  PasswordMismatch: 'password_mismatch',
  EmailMismatch: 'email_mismatch',
  PasswordRequired: 'password_required',
  EmailRequired: 'email_required',
  /** @public Used for password reset flow */ EmailAlreadyInUse: 'email_already_in_use',
} as const;
export type RegistrationStatus = (typeof RegistrationStatus)[keyof typeof RegistrationStatus];

/** @public Unsupported email format reasons - keep in sync with rust/server/src/api/structs.rs */
export const EmailUnsupportedReason = {
  Empty: 'empty',
  MultipleAddresses: 'multiple_addresses',
  DisplayNameOrAngleAddress: 'display_name_or_angle_address',
  CommentsOrCfws: 'comments_or_cfws',
  QuotedLocalPart: 'quoted_local_part',
  NonAscii: 'non_ascii',
  InternalWhitespace: 'internal_whitespace',
  MissingAtSign: 'missing_at_sign',
  MultipleAtSigns: 'multiple_at_signs',
  InvalidLocalPart: 'invalid_local_part',
  InvalidDomain: 'invalid_domain',
  DomainLiteral: 'domain_literal',
} as const;
export type EmailUnsupportedReason =
  (typeof EmailUnsupportedReason)[keyof typeof EmailUnsupportedReason];

/** @public API status values returned when a verification token is consumed - keep in sync with backend */
export const VerifyEmailStatus = {
  /** @public */ Success: 'success',
  /** @public */ InvalidToken: 'invalid_token',
  /** @public */ ExpiredToken: 'expired_token',
  /** @public */ EmailAlreadyInUse: 'email_already_in_use',
} as const;
export type VerifyEmailStatus = (typeof VerifyEmailStatus)[keyof typeof VerifyEmailStatus];

/** @public Request body for completing a pending email verification by token - keep in sync with backend */
export interface VerifyEmailRequest {
  token: string;
}

/** @public Response body for email verification token consumption - keep in sync with backend */
export interface VerifyEmailResponse {
  success: boolean;
  status: VerifyEmailStatus;
}

/** @public API status values for starting a pending email change - keep in sync with backend */
export const UpdateEmailStatus = {
  /** @public */ PendingVerificationStarted: 'pending_verification_started',
  /** @public */ UnsupportedFormat: 'unsupported_format',
  /** @public */ AlreadyVerifiedByYou: 'already_verified_by_you',
  /** @public */ AlreadyPendingForYou: 'already_pending_for_you',
  /** @public */ ClaimedByAnotherUser: 'claimed_by_another_user',
  /** @public */ EmailRequired: 'email_required',
} as const;
export type UpdateEmailStatus = (typeof UpdateEmailStatus)[keyof typeof UpdateEmailStatus];

/** @public Response body for starting a pending email verification - keep in sync with backend */
export interface UpdateEmailResponse {
  success: boolean;
  status: UpdateEmailStatus;
  reason?: EmailUnsupportedReason;
}

/** @public API status values for resending the latest pending email verification - keep in sync with backend */
export const ResendEmailVerificationStatus = {
  /** @public */ VerificationEmailSent: 'verification_email_sent',
  /** @public */ NoPendingVerification: 'no_pending_verification',
} as const;
export type ResendEmailVerificationStatus =
  (typeof ResendEmailVerificationStatus)[keyof typeof ResendEmailVerificationStatus];

/** @public Response body for resending a pending email verification - keep in sync with backend */
export interface ResendEmailVerificationResponse {
  success: boolean;
  status: ResendEmailVerificationStatus;
}

/** @public API status values for abandoning a pending email verification - keep in sync with backend */
export const StopEmailVerificationStatus = {
  /** @public */ PendingVerificationStopped: 'pending_verification_stopped',
  /** @public */ NoPendingVerification: 'no_pending_verification',
} as const;
export type StopEmailVerificationStatus =
  (typeof StopEmailVerificationStatus)[keyof typeof StopEmailVerificationStatus];

/** @public Response body for stopping a pending email verification - keep in sync with backend */
export interface StopEmailVerificationResponse {
  success: boolean;
  status: StopEmailVerificationStatus;
}

// Password reset types - Must be kept in sync with backend (server/src/api/structs.rs)

/** @public API type for password reset - keep in sync with backend */
export interface RequestPasswordResetRequest {
  email: string;
}

/** @public API status values for password reset requests - keep in sync with backend */
export const RequestPasswordResetStatus = {
  /** @public */ Accepted: 'accepted',
  /** @public */ UnsupportedFormat: 'unsupported_format',
} as const;
export type RequestPasswordResetStatusValue =
  (typeof RequestPasswordResetStatus)[keyof typeof RequestPasswordResetStatus];

/** @public API type for password reset - keep in sync with backend */
export interface RequestPasswordResetResponse {
  accepted: boolean;
  status: RequestPasswordResetStatusValue;
  reason?: EmailUnsupportedReason;
}

/** @public API enum for password reset status - keep in sync with backend */
export const ResetPasswordStatus = {
  /** @public */ Success: 'success',
  /** @public */ InvalidToken: 'invalid_token',
  /** @public */ ExpiredToken: 'expired_token',
  /** @public */ PasswordInsecure: 'password_insecure',
} as const;
export type ResetPasswordStatus = (typeof ResetPasswordStatus)[keyof typeof ResetPasswordStatus];

/** @public API type for password reset - keep in sync with backend */
export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

/** @public API type for password reset - keep in sync with backend */
export interface ResetPasswordResponse {
  success: boolean;
  status?: ResetPasswordStatus;
}

/** @public API enum for reset token validation - keep in sync with backend */
export enum ValidateResetTokenStatus {
  Valid = 'valid',
  InvalidToken = 'invalid_token',
  ExpiredToken = 'expired_token',
}

/** @public request type for reset token validation - keep in sync with backend */
export interface ValidateResetTokenRequest {
  token: string;
}

/** @public response type for reset token validation - keep in sync with backend */
export interface ValidateResetTokenResponse {
  success: boolean;
  status: ValidateResetTokenStatus;
}

/**
 * Type of product post to create (review or question)
 * Must be kept in sync with the backend ProductPostType enum
 */
export enum ProductPostType {
  Review = 'review',
  Question = 'question',
}

/**
 * Request to create a new document
 * Must be kept in sync with the backend NewDocRequest struct
 */
export interface NewDocRequest {
  /** Content of the document */
  content: string;

  /** Topic of the document (optional) */
  topic?: string;

  /** Visibility of the document */
  visibility?: DocVisibility;

  /** Citation document id to reference (optional) */
  citation?: PublicDocId;

  /** Type of product post (if applicable) */
  post_type?: ProductPostType;
}

// Dynamically check password strength.
export async function checkPasswordStrength(password: string): Promise<PasswordStrengthResponse> {
  if (!password) {
    return { strength: 0, is_acceptable: false };
  }

  const resp = await fetch(`${API_URL}/auth/password_strength_check`, {
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }

  const data = await resp.json();
  return data;
}

// Validate a password reset token (read-only operation)
export async function validateResetToken(token: string): Promise<ValidateResetTokenResponse> {
  const resp = await fetch(`${API_URL}/auth/validate_reset_token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token } satisfies ValidateResetTokenRequest),
  });

  // Application-level errors are HTTP 200; only network/HTTP errors throw
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return resp.json();
}

/** Posts a verification token and returns the backend's terminal verification result. */
export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const resp = await fetch(`${API_URL}/auth/verify_email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token } satisfies VerifyEmailRequest),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }

  return resp.json();
}

// Check if a username is available
export async function checkUsernameAvailability(
  username: string
): Promise<{ available: boolean; error?: string }> {
  try {
    const resp = await fetch(`${API_URL}/auth/check_username`, {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });

    const data = await resp.json();

    if (resp.ok) {
      return { available: data.available };
    } else {
      return {
        available: false,
        error: data.error || 'Failed to check username availability',
      };
    }
  } catch (error: any) {
    console.error('Error checking username availability:', error);
    return {
      available: false,
      error: error.message || 'Network error',
    };
  }
}

/** Starts verification for a new email while leaving the existing verified email in place. */
export async function startEmailVerification(newEmail: string): Promise<UpdateEmailResponse> {
  const resp = await fetch(`${API_URL}/user/set_email`, {
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_email: newEmail }),
  });

  if (!resp.ok) {
    throw new Error(`Error updating email: ${resp.statusText}`);
  }

  return resp.json();
}

/** Requests a fresh verification email for the current pending email address. */
export async function resendEmailVerification(): Promise<ResendEmailVerificationResponse> {
  const resp = await fetch(`${API_URL}/user/resend_email_verification`, {
    credentials: 'include',
    method: 'POST',
  });

  if (!resp.ok) {
    throw new Error(`Error resending verification email: ${resp.statusText}`);
  }

  return resp.json();
}

/** Abandons the current pending email verification for the signed-in user. */
export async function stopEmailVerification(): Promise<StopEmailVerificationResponse> {
  const resp = await fetch(`${API_URL}/user/stop_email_verification`, {
    credentials: 'include',
    method: 'POST',
  });

  if (!resp.ok) {
    throw new Error(`Error stopping email verification: ${resp.statusText}`);
  }

  return resp.json();
}

/** Payload used for both password-based and OAuth-based registration requests. */
export interface RegisterUserRequest {
  invite_code: string;
  username: string;
  fullname: string;
  email: string;
  email2: string;
  password?: string;
  password2?: string;
  oauth_identity?: OAuthIdentityInfo;
}

/** Registers a new user with either password or OAuth identity data. */
export async function registerUser(registrationData: RegisterUserRequest): Promise<{
  success: boolean;
  status?: RegistrationStatus;
  reason?: EmailUnsupportedReason;
  error?: string;
}> {
  try {
    const res = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important for session cookies
      body: JSON.stringify(registrationData),
    });

    const responseData = await res.json();
    console.log('Registration response:', responseData);

    if (res.ok && responseData.success) {
      return {
        success: true,
        status: responseData.status,
        reason: responseData.reason,
      };
    } else {
      // Return registration status for expected application states
      if (responseData.status) {
        return {
          success: false,
          status: responseData.status,
          reason: responseData.reason,
        };
      } else if (!res.ok) {
        // Handle server errors
        return {
          success: false,
          error: responseData.error?.message || 'Unknown error',
        };
      } else {
        // Generic error
        return {
          success: false,
          error: 'Registration failed',
        };
      }
    }
  } catch (error) {
    console.error('Registration error:', error);

    // Return error information
    if (error instanceof SyntaxError) {
      return {
        success: false,
        error: 'The server returned an invalid response',
      };
    } else {
      return {
        success: false,
        error: 'A network error occurred',
      };
    }
  }
}

// =============================================================================
// Integration Health Dashboard types
// IMPORTANT: Keep in sync with rust/server/src/api/health_types.rs
// =============================================================================

// IMPORTANT: Keep in sync with HealthStatus in rust/server/src/api/health_types.rs
export enum HealthStatus {
  Healthy = 'healthy',
  Degraded = 'degraded',
  Unhealthy = 'unhealthy',
}

// IMPORTANT: Keep in sync with HealthEvent in rust/server/src/api/health_types.rs
export enum HealthEvent {
  Ok = 'ok',
  Timeout = 'timeout',
  Network = 'network',
  Parse = 'parse',
  RateLimited = 'rate_limited',
  Auth = 'auth',
  Server5xx = 'server5xx',
  Client4xx = 'client4xx',
  Other = 'other',
}

// IMPORTANT: Keep in sync with TimestampedHealthEvent in rust/server/src/api/health_types.rs
interface TimestampedHealthEvent {
  time: string;
  event: HealthEvent;
}

// IMPORTANT: Keep in sync with DisplayClientHealthReport.metrics in rust/server/src/api/health_types.rs
export interface HealthMetrics {
  status: HealthStatus;
  total_events: number;
  error_events: number;
  error_rate: number;
  is_blocked: boolean;
  recent_events: TimestampedHealthEvent[];
}

// IMPORTANT: Keep in sync with DisplayClientHealthReport in rust/server/src/api/health_types.rs
export interface ClientHealthReport {
  provider: string;
  metrics: HealthMetrics;
}

// IMPORTANT: Keep in sync with HealthTotals in rust/server/src/api/health_types.rs
export interface HealthTotals {
  total_clients: number;
  healthy: number;
  degraded: number;
  unhealthy: number;
}

// IMPORTANT: Keep in sync with DisplayRoutingEntry in rust/server/src/api/health_types.rs
export interface RoutingEntry {
  provider: string;
  status: HealthStatus;
}

// IMPORTANT: Keep in sync with DisplayCatalogRouting in rust/server/src/api/health_types.rs
export interface CatalogRouting {
  catalog: string;
  active_providers: RoutingEntry[];
  excluded_providers: RoutingEntry[];
}

// IMPORTANT: Keep in sync with DisplayMarketplaceRouting in rust/server/src/api/health_types.rs
export interface MarketplaceRouting {
  marketplace: string;
  active_providers: RoutingEntry[];
  excluded_providers: RoutingEntry[];
  unavailable: boolean;
}

// IMPORTANT: Keep in sync with DisplayOrchestratorHealth in rust/server/src/api/health_types.rs
export interface OrchestratorHealth {
  orchestrator: string;
  totals: HealthTotals;
  clients: ClientHealthReport[];
  catalog_routing?: CatalogRouting[];
  marketplace_routing?: MarketplaceRouting[];
}

// IMPORTANT: Keep in sync with IntegrationHealthReport in rust/server/src/api/structs.rs
export interface IntegrationHealthReport {
  generated_at: string;
  fetch?: OrchestratorHealth;
  search?: OrchestratorHealth;
}
